/**
 * CLI setup, mounted at /cli/setup and linked from the user menu.
 *
 * Three steps, each a command to paste: install, sign in, request. The sign-in
 * step is the one that matters most — see commands.ts for why it names this
 * portal explicitly.
 *
 * Sits beside /cli (the sign-in approval page) rather than under /members
 * because the two are one journey: this page's second step ends with the
 * terminal sending the member to /cli.
 */
import { Box, Flex, Heading, Stack, Text } from "@chakra-ui/react";
import { TbTerminal2 } from "react-icons/tb";

import Card from "components/ds/Card";
import Linker from "ui/Linker";

import CommandBlock from "./CommandBlock";
import { INSTALL_COMMAND, REQUEST_COMMAND, signInCommand } from "./commands";

type Step = { title: string; body: string; command: string };

const StepRow = ({ n, step }: { n: number; step: Step }) => (
  <Flex gap="md" align="flex-start">
    <Flex
      align="center"
      justify="center"
      boxSize="24px"
      borderRadius="full"
      bg="neutral.100"
      color="neutral.800"
      fontSize="xs"
      fontWeight={600}
      flexShrink={0}
      mt="2px"
    >
      {n}
    </Flex>
    <Stack spacing="sm" flex={1} minW={0}>
      <Box>
        <Text fontSize="md" fontWeight={600} color="neutral.950">
          {step.title}
        </Text>
        <Text fontSize="smd" color="neutral.700" mt="2px">
          {step.body}
        </Text>
      </Box>
      <CommandBlock command={step.command} />
    </Stack>
  </Flex>
);

const CliSetup = () => {
  const steps: Step[] = [
    {
      title: "Install",
      body: "Works on macOS and Linux. The installer checks the download before it installs anything. Run it again any time to update.",
      command: INSTALL_COMMAND,
    },
    {
      title: "Sign in",
      body: "Your terminal shows a code and a link back to this portal. Open the link and type the code to approve it. You stay signed in on that machine for up to 30 days.",
      command: signInCommand(window.location.origin),
    },
    {
      title: "Request access",
      body: "Pick what you need, for how long and why. Name the resource and add --for 8h to skip those steps, like ploy request GitHub --for 8h.",
      command: REQUEST_COMMAND,
    },
  ];

  return (
    <Box maxW="640px" mx="auto" w="full" py="lg">
      <Card>
        <Stack spacing="xl">
          <Stack spacing="sm">
            <Flex gap="md" align="center">
              <Flex
                align="center"
                justify="center"
                boxSize="40px"
                borderRadius="full"
                bg="p.50"
                color="p.600"
                flexShrink={0}
              >
                <Box as={TbTerminal2} boxSize="20px" aria-hidden />
              </Flex>
              <Heading
                as="h1"
                fontSize="xl"
                fontWeight={600}
                color="neutral.950"
              >
                Set up the command line tool
              </Heading>
            </Flex>
            <Text fontSize="smd" color="neutral.700">
              Request access from your terminal with{" "}
              <Box as="code" fontFamily="mono">
                ploy
              </Box>
              , without opening the portal.
            </Text>
          </Stack>

          {steps.map((step, i) => (
            <StepRow key={step.title} n={i + 1} step={step} />
          ))}
        </Stack>
      </Card>

      <Text fontSize="xs" color="neutral.700" mt="lg" px="2xs">
        Already have a code in your terminal?{" "}
        <Linker to="/cli" color="p.600" fontWeight={500}>
          Approve the sign-in
        </Linker>
      </Text>
    </Box>
  );
};

export default CliSetup;
